import { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import {
  Badge, Box, Button, Card, CardHeader, CardBody, Grid, GridItem, Heading, 
  HStack, Icon, IconButton, Input, InputGroup, InputRightElement, Select, Tabs,
  TabList, Tab, Text, useColorModeValue, VStack,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { StatCard} from '@/components/dashboard'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  dateFromSessionValue,
  sessionListStateKey,
  syncSearchInputRef,
  useSessionListState,
} from '@/hooks/useSessionListState'
import { Sun, Moon, Wallet, TrendingDown, Landmark, ArrowRightLeft, 
         ChevronLeft, ChevronRight, Search, Calendar } from 'lucide-react'

type TabValue = 'all' | 'trash'
type BudgetFilterType = 'weekly' | 'daily'
type DayNightMode = 'Day' | 'Night'

type BudgetStats = {
  total_budget: number
  total_expense: number
  cash_on_hand: number
  previous_cash_on_hand: number
  previous_cash_on_hand_date: string
}

const EXPENSE_TYPE_OPTIONS = [
  { value: 'All', label: 'All Expenses' },
  { value: 'Issued Budget', label: 'Issued Budget' },
  { value: 'Truck Expense', label: 'Truck Expense' },
  { value: 'Parts Expense', label: 'Parts Expense' },
  { value: 'Other Expense', label: 'Other Expense' },
  { value: 'Driver Cash Advance', label: 'Driver Cash Advance' },
  { value: 'Helper Cash Advance', label: 'Helper Cash Advance' },
]

function toStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function formatDateForApi(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function startOfWeekMonday(date: Date): Date {
  const weekStart = toStartOfDay(date)
  const day = weekStart.getDay()
  weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1))
  return weekStart
}

function endOfWeekSunday(weekStart: Date): Date {
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 6)
  return weekEnd
}

function isDateWithinRange(date: Date, start: Date, end: Date): boolean {
  const base = toStartOfDay(date).getTime()
  return base >= start.getTime() && base <= end.getTime()
}

export default function BudgetManagementPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const datePickerRef = useRef<HTMLInputElement>(null)
  const { showWarning } = useToastNotification()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)

  // Date Filter
  const [filterType, setFilterType] = useState<BudgetFilterType>('weekly')
  const [currentDate, setCurrentDate] = useState(new Date())
  
  // Chakra UI Color Mode Values
  const bgColor = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const textColor = useColorModeValue('gray.800', 'whiteAlpha.900')
  
  // Expense Type
  const [expenseType, setExpenseType] = useState<string>('All')
  // Stat Cards
  const [stats, setStats] = useState<BudgetStats>({
    total_budget: 0,
    total_expense: 0,
    cash_on_hand: 0,
    previous_cash_on_hand: 0,
    previous_cash_on_hand_date: '',
  })
  // Shift
  const [dayNightMode, setMode] = useState<DayNightMode>('Day')
  const access = useAccessCheck()

  useSessionListState({
    key: sessionListStateKey('budget'),
    values: {
      activeTab,
      searchQuery,
      filterType,
      currentDate: currentDate.getTime(),
      expenseType,
      dayNightMode,
    },
    onRestore: (raw) => {
      if (raw.activeTab === 'all' || raw.activeTab === 'trash') {
        setActiveTab(raw.activeTab)
      }
      if (typeof raw.searchQuery === 'string') {
        setSearchQuery(raw.searchQuery)
        syncSearchInputRef(searchRef, raw.searchQuery)
      }
      if (raw.filterType === 'weekly' || raw.filterType === 'daily') {
        setFilterType(raw.filterType)
      }
      const restoredDate = dateFromSessionValue(raw.currentDate)
      if (restoredDate) setCurrentDate(restoredDate)
      if (typeof raw.expenseType === 'string' && raw.expenseType.length > 0) {
        setExpenseType(raw.expenseType)
      }
      if (raw.dayNightMode === 'Day' || raw.dayNightMode === 'Night') {
        setMode(raw.dayNightMode)
      }
    },
  })

  const buildBudgetActionUrl = useCallback((row: any, action: 'Trash' | 'Delete' | 'Restore') => {
    const sourceTable = row?.source_table
    const sourceId = row?.source_id
    if (!sourceTable || sourceId == null) {
      return ''
    }
    if (action === 'Trash') {
      return `/budget/summary/${sourceTable}/${sourceId}`
    }
    if (action === 'Restore') {
      return `/archived/budget/summary/restore/${sourceTable}/${sourceId}`
    }
    return `/archived/budget/summary/${sourceTable}/${sourceId}`
  }, [])

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        type: {
          name: 'Expense Type',
          // withSort: true,
          maxWidth: '180px',
        },

        description: {
          name: 'Description',
          maxWidth: '300px',
        },

        amount: {
          name: 'Amount',
          // withSort: true,
          maxWidth: '150px',
        },

        shift: {
          name: 'Shift',
          maxWidth: '120px',
        },

        transaction_date: {
          name: 'Transaction Date',
          // withSort: true,
          maxWidth: '180px',
        },
      },
        dataSource: activeTab === 'trash' ? '/archived/budget/summary' : '/budget/summary',
        primaryKey: 'row_key',
        softDelete: true,
        displayInModal: true,
        noRecordText: activeTab === 'trash' ? 'No trashed budget records found' : 'No Budget Records found',
        buildActionUrl: buildBudgetActionUrl,
      }),
    [activeTab, buildBudgetActionUrl]
  )

  const statCards = useMemo(
    () => [
      {
        title:
          filterType === 'weekly'
            ? `Total Budget\n(Only all Issued Budgets)`
            : 'Total Budget\n(Includes Cash Carry Over)',
        value: stats.total_budget,
        icon: Wallet,
        color: 'green',
      },
      {
        title: 'Total Expense \n',
        value: stats.total_expense,
        icon: TrendingDown,
        color: 'red',
      },
      {
        title: 'Cash on Hand',
        value: stats.cash_on_hand,
        icon: Landmark,
        color: 'blue',
      },
      {
        title: 'Cash on Hand\n(Previous Shift)',
        value: stats.previous_cash_on_hand,
        icon: ArrowRightLeft,
        color: 'purple',
      },
    ],
    [filterType, stats]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: false,
      can_import: false,
    }),
    [access.can_edit, access.can_delete, access.can_create, access.can_view]
  )

  const showSubSubCallback = useCallback((all: number, trashed: number) => {
    setTotalRows(all)
    setTotalTrash(trashed)
  }, [])

  const getDateRangeForFilter = useCallback((date: Date, selectedFilter: BudgetFilterType) => {
    const baseDate = toStartOfDay(date)

    if (selectedFilter === 'weekly') {
      const start = startOfWeekMonday(baseDate)
      const end = endOfWeekSunday(start)

      const todayBase = toStartOfDay(new Date())
      const currentWeekStart = startOfWeekMonday(todayBase)
      const currentWeekEnd = endOfWeekSunday(currentWeekStart)
      const isCurrentWeek = isDateWithinRange(baseDate, currentWeekStart, currentWeekEnd)

      return {
        start: formatDateForApi(start),
        end: formatDateForApi(isCurrentWeek ? todayBase : end),
      }
    }

    return {
      start: formatDateForApi(baseDate),
      end: formatDateForApi(baseDate),
    }
  }, [])

  const adjustDate = useCallback((direction: number) => {
    const newDate = new Date(currentDate)
    if (filterType === 'weekly') {
      newDate.setDate(newDate.getDate() + direction * 7)
    } else {
      newDate.setDate(newDate.getDate() + direction)
    }
    setCurrentDate(newDate)
  }, [currentDate, filterType])

  const handleDatePickerChange = useCallback((value: string) => {
    if (!value) return

    const [year, month, day] = value.split('-').map(Number)
    const parsedDate = new Date(year, month - 1, day)
    setCurrentDate(parsedDate)
  }, [])

  // Front End Display of the date range based on the filter type (weekly or monthly)
  const dateRangeDisplay = useMemo(() => {
    const start = toStartOfDay(currentDate)
    if (filterType === 'weekly') {
        const weekStart = startOfWeekMonday(start)
        const weekEnd = endOfWeekSunday(weekStart)
        const todayBase = toStartOfDay(new Date())
        const currentWeekStart = startOfWeekMonday(todayBase)
        const currentWeekEnd = endOfWeekSunday(currentWeekStart)
        const visibleEnd = isDateWithinRange(start, currentWeekStart, currentWeekEnd) ? todayBase : weekEnd

        return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${visibleEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    }
    return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }, [currentDate, filterType])

  const tableParams = useMemo(() => {
    const { start, end } = getDateRangeForFilter(currentDate, filterType)

    return {
      search: searchQuery,
      shift: dayNightMode,
      type: expenseType,
      transaction_date_from: start,
      transaction_date_to: end,
      dateFilterType: filterType,
    }
  }, [searchQuery, dayNightMode, expenseType, currentDate, filterType, getDateRangeForFilter])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = useCallback(() => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }, [handleSearch])

  const fetchBudgetStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/budget/summary', {
        params: tableParams,
      })
      const data = response.data

      setStats({
        total_budget: Number(data.total_budget ?? 0),
        total_expense: Number(data.total_expense ?? 0),
        cash_on_hand: Number(data.cash_on_hand ?? 0),
        previous_cash_on_hand: Number(data.previous_cash_on_hand ?? 0),
        previous_cash_on_hand_date: String(data.previous_cash_on_hand_date ?? ''),
      })
    } catch (error) {
      console.error('Error fetching stats', error)
    }
  }, [tableParams])

  useEffect(() => {
    fetchBudgetStats()
  }, [fetchBudgetStats])

  useEffect(() => {
    if (activeTab === 'trash' && totalTrash === 0) {
      setActiveTab('all')
      setSearchQuery('')
      if (searchRef.current) {
        searchRef.current.value = ''
      }
      tableRef.current?.clearPage()
      setTimeout(() => {
        tableRef.current?.reload()
      }, 100)
    }
  }, [totalTrash, activeTab])

  const convertExpenseType = useCallback((text: string) => {
    return text.toLowerCase().replace(/\s+/g, '-')
  }, [])
  
  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        {/* Stat Cards */}
          <Box pb={2} flexShrink={0} overflowX="auto" overflowY="hidden">
            <Grid
              templateColumns={`repeat(${statCards.length}, minmax(220px, 1fr))`}
              gap={{ base: 3, md: 6 }}
              w="max-content"
              minW="100%"
              sx={{
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
              }}
            >
              {statCards.map((stat, index) => (
                <GridItem key={index}>
                  <Box
                    sx={{
                      '.chakra-card__body': {
                        padding: { base: 3, md: 5 },
                      },
                      '.chakra-text': {
                        fontSize: { base: 'xs', md: 'sm' },
                        minH: { base: '2.6em', md: '2.8em' },
                        lineHeight: 'short',
                      },
                      '.chakra-stat__number': {
                        fontSize: { base: 'xl', md: '2xl' },
                      },
                      'svg': {
                        width: '1.5rem',
                        height: '1.5rem',
                      },
                      '@media (min-width: 48em)': {
                        'svg': {
                          width: '2rem',
                          height: '2rem',
                        },
                      },
                    }}
                  >
                    <StatCard
                      title={stat.title}
                      value={stat.value}
                      icon={stat.icon}
                      color={stat.color}
                    />
                  </Box>
                </GridItem>
              ))}
            </Grid>
          </Box>
        

        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <HStack justify="space-between" align="center" spacing={4}>
                <VStack align="flex-start" spacing={0}>
                  <Heading size="md" mb={1}>
                    Budgets
                  </Heading>
                  <Text fontSize="sm" color={colors.text}>
                    Manage Budget records
                  </Text>
                </VStack>

                {/* Day/Night Mode Toggle */}
                <HStack spacing={4}>
                  {/* Double Button Toggle  */}
                  <Button
                    size="sm"
                    leftIcon={<Sun size={16} />}
                    variant={dayNightMode === 'Day' ? 'solid' : 'outline'}
                    onClick={() => setMode('Day')}
                  >
                    Day
                  </Button>

                  <Button
                    size="sm"
                    leftIcon={<Moon size={16} />}
                    variant={dayNightMode === 'Night' ? 'solid' : 'outline'}
                    onClick={() => setMode('Night')}
                  >
                    Night
                  </Button>

                </HStack>
              </HStack>
            </CardHeader>

            

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/budget-management"
                      createLabel="Create New Budget Record"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack
                spacing={6}
                flexWrap="nowrap"
                overflowX="auto"
                overflowY="hidden"
                sx={{
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'thin',
                }}
              >
                {/* Expense Type Selection */}
                <HStack spacing={4} align="center" flexShrink={0}>
                  <HStack spacing={1}>
                    <Select
                      size="sm"
                      maxW="220px"
                      value={expenseType}
                      onChange={(e) => setExpenseType(e.target.value)}
                      >
                      {EXPENSE_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </HStack>
                </HStack>

                {/* Date Filter */}
                <Select 
                    size="sm" w="140px" borderRadius="md" 
                    value={filterType} 
                    onChange={(e) => setFilterType(e.target.value as BudgetFilterType)}
                >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                </Select>

                <HStack spacing={1}>
                    <IconButton icon={<ChevronLeft size={18} />} aria-label="Prev" size="sm" variant="ghost" onClick={() => adjustDate(-1)} />
                    <HStack 
                        border="1px solid" 
                        borderColor={borderColor} // Dynamic border
                        px={4} 
                        py={1.5} 
                        borderRadius="md" 
                        bg={bgColor} // Dynamic background
                        cursor="pointer" 
                        position="relative" 
                        _hover={{ borderColor: "blue.400" }}
                        onClick={() => datePickerRef.current?.showPicker()}
                    >
                        <Icon as={Calendar} size={14} color="blue.500" />
                          <Text 
                              fontWeight="bold" 
                              fontSize="sm" 
                              minW="150px" 
                              textAlign="center"
                              color={textColor} // Dynamic text color
                          >
                              {dateRangeDisplay}
                          </Text>
                        <input 
                          ref={datePickerRef}
                            id="report-date-picker" 
                            type="date" 
                            style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                            onChange={(e) => e.target.value && handleDatePickerChange(e.target.value)}
                        />
                    </HStack>
                    <IconButton icon={<ChevronRight size={18} />} aria-label="Next" size="sm" variant="ghost" onClick={() => adjustDate(1)} />
                </HStack>
      

                <Box flex="1 1 280px" minW="150px" maxW="280px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={{...tableOptions,
                    showCheckboxes: false,
                  }}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onDeleteSuccess={fetchBudgetStats}
                  onOpenModal={(row) => {
                    const sourceId = row?.source_id
                    if (sourceId == null || sourceId === '') {
                      showWarning(
                        'Cannot open editor',
                        'This row has no source record id; it cannot be edited.'
                      )
                      return
                    }
                    const expenseSlug = convertExpenseType(String(row?.type ?? ''))
                    navigate(`/budget-management/${expenseSlug}/${sourceId}`)
                  }}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
